import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/field";
import { ErrorState, Loading } from "@/components/query-state";
import { ApiError } from "@/lib/api";
import type { Domain, DomainMember, EligibleMember } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { useHolyModeOptional } from "@/preferences/holy-mode";

type MemberPickerProps = {
  id: string;
  label: string;
  members: EligibleMember[];
  savedMembers: DomainMember[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  excludedIds?: string[];
  disabled?: boolean;
};

function MemberPicker({ id, label, members, savedMembers, selectedIds, onChange, multiple = false, excludedIds = [], disabled = false }: MemberPickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const blurTimeout = useRef<number | undefined>(undefined);
  const selected = selectedIds.map((memberId) => {
    const eligible = members.find((member) => member.id === memberId);
    const saved = savedMembers.find((member) => member.userId === memberId);
    return {
      id: memberId,
      name: eligible?.name ?? saved?.name ?? `#${memberId}`,
      email: eligible ? eligible.email : saved?.email ?? null,
      canWrite: Boolean(eligible),
    };
  });
  const blocked = new Set(excludedIds);
  const filtered = members.filter((member) => {
    if (blocked.has(member.id)) return false;
    const text = `${member.name} ${member.email ?? ""}`.toLocaleLowerCase();
    return text.includes(query.trim().toLocaleLowerCase());
  });
  const listId = `${id}-options`;
  const activeId = filtered[activeIndex] ? `${id}-option-${filtered[activeIndex].id}` : undefined;

  const choose = (member: EligibleMember) => {
    if (multiple) {
      onChange(selectedIds.includes(member.id)
        ? selectedIds.filter((id) => id !== member.id)
        : [...selectedIds, member.id]);
    } else {
      onChange([member.id]);
    }
    setQuery("");
    setActiveIndex(0);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open && filtered[activeIndex]) {
      event.preventDefault();
      choose(filtered[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => () => {
    if (blurTimeout.current !== undefined) window.clearTimeout(blurTimeout.current);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selected.map((member) => (
            <span key={member.id} className="bg-surface border-border-subtle inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs">
              <span>
                {member.name}{member.email ? ` · ${member.email}` : ""}
                {!member.canWrite ? <span className="text-danger ml-1">{t("domains.noWritePermission")}</span> : null}
              </span>
              <button
                type="button"
                className="text-muted hover:text-foreground"
                aria-label={t(multiple ? "domains.removeSubstitute" : "domains.removeOwner", { name: member.name })}
                onClick={() => onChange(selectedIds.filter((memberId) => memberId !== member.id))}
              >×</button>
            </span>
          ))}
        </div>
      ) : null}
      <Input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? activeId : undefined}
        placeholder={t("domains.memberSearch")}
        value={query}
        disabled={disabled}
        onFocus={() => {
          if (blurTimeout.current !== undefined) window.clearTimeout(blurTimeout.current);
          blurTimeout.current = undefined;
          setOpen(true);
        }}
        onBlur={() => {
          blurTimeout.current = window.setTimeout(() => {
            blurTimeout.current = undefined;
            setOpen(false);
          }, 100);
        }}
        onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {open && filtered.length > 0 ? (
        <ul id={listId} role="listbox" aria-label={label} className="border-border-subtle bg-surface max-h-48 overflow-auto rounded-md border">
          {filtered.map((member) => (
            <li
              id={`${id}-option-${member.id}`}
              key={member.id}
              role="option"
              aria-selected={selectedIds.includes(member.id)}
              className={`cursor-pointer px-3 py-2 text-sm ${member.id === filtered[activeIndex]?.id ? "bg-surface-raised" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(member)}
            >
              <span className="block">{member.name}</span>
              {member.email ? <span className="text-muted block text-xs">{member.email}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {open && filtered.length === 0 ? <p role="status" className="text-muted px-3 py-2 text-sm">{t("domains.memberSearchEmpty")}</p> : null}
    </div>
  );
}

function MembersForm({ domain }: { domain: Domain }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const currentMembers = domain.members ?? [];
  const memberDirectory = useQuery({ queryKey: ["eligible-members"], queryFn: api.eligibleMembers });
  const [owner, setOwner] = useState(currentMembers.find((member) => member.role === "owner")?.userId ?? "");
  const [subs, setSubs] = useState(currentMembers.filter((member) => member.role === "substitute").map((member) => member.userId));
  const save = useMutation({
    mutationFn: () => {
      return api.replaceMembers(domain.key, [
        { userId: owner, role: "owner" },
        ...subs.map((userId) => ({ userId, role: "substitute" as const })),
      ]);
    },
    onSuccess: () => { void client.invalidateQueries({ queryKey: ["domains"] }); },
  });
  const directoryReady = memberDirectory.isSuccess && memberDirectory.data.length > 0;
  const validOwner = directoryReady && memberDirectory.data.some((member) => member.id === owner);
  const hasIneligibleSubstitute = memberDirectory.isSuccess && subs.some((id) => !memberDirectory.data.some((member) => member.id === id));
  const rejectedIds = (() => {
    if (!save.isError || !(save.error instanceof ApiError)) return [];
    const body = save.error.body;
    if (!body || typeof body !== "object" || !("rejectedIds" in body) || !Array.isArray(body.rejectedIds)) return [];
    return body.rejectedIds.filter((id): id is string => typeof id === "string");
  })();
  return (
    <form
      className="border-border-subtle mt-3 flex flex-col gap-3 border-t pt-3"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      {memberDirectory.isPending ? <p className="text-muted text-sm" role="status">{t("domains.membersLoading")}</p> : null}
      {memberDirectory.isError ? (
        <div role="alert" className="flex flex-col gap-1 text-sm">
          <span className="text-danger">{t("domains.membersUnavailable")}</span>
          <span className="text-muted">{memberDirectory.error.message}</span>
        </div>
      ) : null}
      {memberDirectory.isSuccess && memberDirectory.data.length === 0 ? <p className="text-muted text-sm">{t("domains.membersEmpty")}</p> : null}
      {memberDirectory.isSuccess ? <>
      <Field label={t("domains.owner")} htmlFor={`owner-${domain.key}`}>
        <MemberPicker
          id={`owner-${domain.key}`}
          label={t("domains.owner")}
          members={memberDirectory.data}
          savedMembers={currentMembers}
          selectedIds={owner ? [owner] : []}
          onChange={(ids) => { setOwner(ids[0] ?? ""); setSubs((current) => current.filter((id) => id !== ids[0])); }}
          disabled={save.isPending}
        />
      </Field>
      <Field label={t("domains.substitutes")} htmlFor={`subs-${domain.key}`}>
        <MemberPicker
          id={`subs-${domain.key}`}
          label={t("domains.substitutes")}
          members={memberDirectory.data}
          savedMembers={currentMembers}
          selectedIds={subs}
          onChange={setSubs}
          multiple
          excludedIds={owner ? [owner] : []}
          disabled={save.isPending}
        />
      </Field>
      </> : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!validOwner || hasIneligibleSubstitute || save.isPending}>{t("domains.saveMembers")}</Button>
        {save.isSuccess ? <span className="text-sm text-[#2f6b21]">{t("domains.membersSaved")}</span> : null}
        {save.isError ? (
          <div role="alert" className="text-danger flex flex-col text-sm">
            <span>{save.error.message}</span>
            {rejectedIds.length ? <span>{t("domains.rejectedIds", { ids: rejectedIds.join(", ") })}</span> : null}
          </div>
        ) : null}
      </div>
    </form>
  );
}

export function DomainsPage() {
  const { t } = useTranslation();
  const { enabled: holyMode } = useHolyModeOptional();
  const client = useQueryClient();
  const domains = useQuery({ queryKey: ["domains"], queryFn: api.listDomains });
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createDomain(key.trim(), name.trim()),
    onSuccess: () => { setKey(""); setName(""); void client.invalidateQueries({ queryKey: ["domains"] }); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); create.mutate(); };

  return (
    <>
      <PageHeader title={t("domains.title")} description={t("domains.description")} sacredIcon="church" sacredCaption="holy.domainsCaption" />
      {domains.isPending ? <Loading /> : domains.isError ? <ErrorState error={domains.error} /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="flex min-w-0 flex-col gap-3">
            {domains.data.map((d) => (
              <Card key={d.key}>
                <CardBody>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 break-words">
                      <p className="text-foreground text-sm font-medium">{d.name}</p>
                      <p className="text-muted text-xs">{d.key}</p>
                      {d.members?.length ? <div className="mt-2 flex flex-col gap-1">
                        {(["owner", "substitute"] as const).map((role) => {
                          const roleMembers = d.members!.filter((member) => member.role === role);
                          return <p key={role} className="text-muted text-xs">
                            <span className="font-medium">{t(role === "owner" ? "domains.currentOwner" : "domains.currentSubstitutes")}: </span>
                            {roleMembers.length ? roleMembers.map((member) => <span key={member.userId} className="mr-2"><span>{member.name || `#${member.userId}`}</span>{member.email ? <> (<span>{member.email}</span>)</> : null}</span>) : t(role === "owner" ? "domains.noOwner" : "domains.noSubstitutes")}
                          </p>;
                        })}
                      </div> : null}
                    </div>
                    <Button variant="secondary" size="sm" aria-label={t(holyMode ? "domains.editFaithful" : "domains.editMembers", { name: d.name })} onClick={() => setEditing(editing === d.key ? null : d.key)}>
                      {t(holyMode ? "domains.editFaithfulButton" : "domains.editMembersButton")}
                    </Button>
                  </div>
                  {editing === d.key ? <MembersForm domain={d} /> : null}
                </CardBody>
              </Card>
            ))}
          </div>
          <Card className="h-fit">
            <CardHeader><CardTitle sacredIcon="church">{t("domains.newDomain")}</CardTitle></CardHeader>
            <CardBody>
              <form className="flex flex-col gap-3" onSubmit={submit}>
                <Field label={t("domains.key")} htmlFor="domain-key" hint={t("domains.keyHint")}>
                  <Input id="domain-key" value={key} onChange={(e) => setKey(e.target.value)} />
                </Field>
                <Field label={t("domains.name")} htmlFor="domain-name">
                  <Input id="domain-name" value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                <Button type="submit" disabled={!key.trim() || !name.trim() || create.isPending}>{t("domains.create")}</Button>
                {create.isError ? <p role="alert" className="text-danger text-sm">{create.error.message}</p> : null}
              </form>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}
